import { Skeleton } from '@music-vine/cadence/ui';
import { cn } from '@/lib/utils';
const CHIP_WIDTHS = ['w-32', 'w-28', 'w-40', 'w-24', 'w-36', 'w-44', 'w-28'];
type SceneSkeletonProps = {
  className?: string;
};

/**
 * Default loading placeholder for dialog-stage scenes. Mirrors the
 * questionnaire's structure (step pills, icon block, title, subtitle,
 * option chips, CTA row) so the modal keeps a stable size before content
 * arrives — without a skeleton, scenes that briefly render `null` collapse
 * the modal to a thin strip while their data loads.
 *
 * Use this anywhere a scene has a transient empty state — auth-gate
 * redirects, queries in flight, redirects in flight, etc. — so the user
 * always sees a consistent "loading" surface.
 */
const SceneSkeleton = ({
  className
}: SceneSkeletonProps) => <div aria-label="Loading" className={cn('flex cursor-default flex-col gap-6 p-8', className)} data-sentry-component="SceneSkeleton" data-sentry-source-file="scene-skeleton.tsx">
    <div className="flex items-center gap-2">
      <Skeleton className="h-1.5 w-10 rounded-full" data-sentry-element="Skeleton" data-sentry-source-file="scene-skeleton.tsx" />
      <Skeleton className="h-1.5 w-4 rounded-full" data-sentry-element="Skeleton" data-sentry-source-file="scene-skeleton.tsx" />
      <Skeleton className="h-1.5 w-4 rounded-full" data-sentry-element="Skeleton" data-sentry-source-file="scene-skeleton.tsx" />
      <Skeleton className="h-1.5 w-4 rounded-full" data-sentry-element="Skeleton" data-sentry-source-file="scene-skeleton.tsx" />
    </div>

    <div className="flex items-start gap-4">
      <Skeleton className="size-[52px] shrink-0 rounded-xl" data-sentry-element="Skeleton" data-sentry-source-file="scene-skeleton.tsx" />
      <div className="flex-1 space-y-2 pt-1">
        <Skeleton className="h-7 w-3/4" data-sentry-element="Skeleton" data-sentry-source-file="scene-skeleton.tsx" />
        <Skeleton className="h-5 w-1/2" data-sentry-element="Skeleton" data-sentry-source-file="scene-skeleton.tsx" />
      </div>
    </div>

    <div className="flex flex-wrap gap-2">
      {CHIP_WIDTHS.map(width => <Skeleton className={cn('h-9 rounded-full', width)} key={width} />)}
    </div>

    <div className="flex items-center justify-end pt-4">
      <Skeleton className="h-11 w-20 rounded-full" data-sentry-element="Skeleton" data-sentry-source-file="scene-skeleton.tsx" />
    </div>
  </div>;
export default SceneSkeleton;

/**
 * Thin scene wrapper around {@link SceneSkeleton} so it can be registered
 * in the scene registry (which requires `ComponentType<SceneProps>`). Used
 * by the Admin Bar's Modal Preview tool to inspect the skeleton on its
 * own — not part of any real flow.
 */
export const SceneSkeletonPreviewScene = () => <SceneSkeleton data-sentry-element="SceneSkeleton" data-sentry-component="SceneSkeletonPreviewScene" data-sentry-source-file="scene-skeleton.tsx" />;